import { useTranslation } from 'react-i18next';
import { Pie, PieChart, Cell } from 'recharts';

import { BudgetHoverOverlay } from '@/features/charts/components/chart-budget-overlay';
import { RedSvgGradient } from '@/features/charts/components/chart-gradients';
import { BasicGradientWarningIcon } from '@/features/charts/components/chart-warning-icon';
import { AggregationType } from '@/features/charts/components/create-chart-type-selection';
import { useBudgetOverlay } from '@/features/charts/hooks/use-budget-overlay';
import {
  ChartResult,
  calculateChartStats,
} from '@/features/charts/util/chart-util';
import { useKpiTranslations } from '@/features/projects/project-details/components/kpi-card';
import { ChartContainer, ChartTooltip } from '@/shared/components/ui/chart';
import { ChartTooltipContentSingle } from '@/shared/components/ui/custom-chart-tooltip';
import { getKpiUnitAbbreviationTranslation } from '@/shared/hooks/use-kpi-unit-translation';

export const CustomPieChart = ({
  chartResult,
  maxValue,
  className,
  aggregationType,
}: {
  chartResult: ChartResult;
  maxValue?: number;
  className?: string;
  aggregationType: AggregationType;
}) => {
  const { t: tChart } = useTranslation('chart');
  const { chartData, chartConfig } = chartResult;
  const kpiLength = Object.keys(chartConfig).length;

  const unit = chartData[0]?.unit;
  const { tKpiTypes } = useKpiTranslations();
  const unitAbbreviation = getKpiUnitAbbreviationTranslation(
    tKpiTypes,
    unit || '',
  );

  const { max } = calculateChartStats({
    chartData: chartData,
    aggregationType: aggregationType,
  });

  const chartId = crypto.randomUUID();
  const {
    isBudgetHovered,
    mousePosition,
    handleBudgetMouseEnter,
    handleBudgetMouseLeave,
  } = useBudgetOverlay();

  let finalChartData = chartData;

  if (maxValue && finalChartData.length === 1) {
    // Create a copy to avoid mutating the original chartData array
    const currValue = finalChartData[0].value ?? 0;
    const exceededBudget = currValue > maxValue;
    const diff = exceededBudget ? 0 : maxValue - currValue;
    finalChartData = [
      ...finalChartData,
      {
        name: tChart('rest_budget_label'),
        value: diff,
        fill: 'transparent',
        aggregationType: AggregationType.ACCUMULATED,
        budget: maxValue,
        kpiName: '',
        kpiId: -1,
      },
    ];
  }

  return (
    <div className="flex flex-col items-center">
      {max > (maxValue ?? 0) && (
        <BasicGradientWarningIcon
          maxValue={maxValue ?? 0}
          currValue={max}
          unit={unitAbbreviation}
        />
      )}
      <ChartContainer
        config={chartConfig}
        className={`mx-auto max-h-[250px] pb-0 [&_.recharts-pie-label-text]:fill-foreground ${className || ''}`}
        onMouseLeave={handleBudgetMouseLeave}
      >
        <PieChart>
          <defs>
            <RedSvgGradient />
            <radialGradient
              id={`restBudgetGradient-${chartId}`}
              cx="50%"
              cy="50%"
              r="50%"
              gradientUnits="objectBoundingBox"
            >
              <stop
                offset="0%"
                stopColor="hsl(0, 0%, 30%)"
                stopOpacity="0.02"
              />
              <stop
                offset="85%"
                stopColor="hsl(0, 0%, 40%)"
                stopOpacity="0.03"
              />
              <stop
                offset="92%"
                stopColor="hsl(0, 0%, 60%)"
                stopOpacity="0.06"
              />
              <stop
                offset="97%"
                stopColor="hsl(0, 0%, 75%)"
                stopOpacity="0.12"
              />
              <stop
                offset="100%"
                stopColor="hsl(0, 0%, 85%)"
                stopOpacity="0.15"
              />
            </radialGradient>
            {finalChartData.map((entry, index) => {
              const baseColor = (entry.fill as string) || '#8884d8';
              return (
                <radialGradient
                  key={`cellGradient-${chartId}-${index}`}
                  id={`cellGradient-${chartId}-${index}`}
                  cx="50%"
                  cy="50%"
                  r="50%"
                  gradientUnits="objectBoundingBox"
                >
                  <stop offset="0%" stopColor={baseColor} stopOpacity="0.6" />
                  <stop offset="75%" stopColor={baseColor} stopOpacity="0.7" />
                  <stop offset="100%" stopColor={baseColor} stopOpacity="0.8" />
                </radialGradient>
              );
            })}
          </defs>
          <ChartTooltip
            content={(props) => {
              if (props.payload?.[0]?.dataKey === 'background') {
                return null;
              }
              const payloadItem = props.payload?.[0]?.payload;
              if (payloadItem?.kpiId === -1) {
                return null;
              }
              if (!props.active && isBudgetHovered) {
                handleBudgetMouseLeave();
              }
              return <ChartTooltipContentSingle {...props} />;
            }}
          />
          {
            <Pie
              data={[{ background: 1 }]}
              dataKey="background"
              outerRadius={'90%'}
              fill={`url(#restBudgetGradient-${chartId})`}
              isAnimationActive={false}
              tooltipType="none"
            />
          }
          <Pie
            data={finalChartData}
            cornerRadius={7}
            dataKey="value"
            nameKey="name"
            outerRadius="85%"
          >
            {finalChartData.map((entry, index) => {
              const exceededBudget =
                entry.budget &&
                entry.value &&
                Number(entry.value) > entry.budget;
              const isRestBudget = entry.kpiId === -1;
              return (
                <Cell
                  key={`cell-${index}`}
                  fill={
                    exceededBudget
                      ? 'url(#redSvgGradient)'
                      : `url(#cellGradient-${chartId}-${index})`
                  }
                  strokeWidth={0.75}
                  stroke={
                    index === finalChartData.length - 1 && kpiLength === 1
                      ? ''
                      : 'hsla(0, 0%, 100%, 0.5)'
                  }
                  onMouseEnter={
                    isRestBudget ? handleBudgetMouseEnter : undefined
                  }
                  onMouseLeave={
                    isRestBudget ? handleBudgetMouseLeave : undefined
                  }
                />
              );
            })}
          </Pie>
        </PieChart>
      </ChartContainer>
      {maxValue && finalChartData.length > 1 && (
        <BudgetHoverOverlay
          isVisible={isBudgetHovered}
          mouseX={mousePosition.x}
          mouseY={mousePosition.y}
          maxValue={maxValue}
          currValue={finalChartData[0]?.value ?? 0}
          unit={unitAbbreviation}
        />
      )}
    </div>
  );
};
